import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import dealerSocketService from '../services/dealerSocketService';
import notificationSound from '../utils/notificationSound';

export const useDealerNotifications = (token) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  // Connect to dealer socket
  useEffect(() => {
    if (token) {
      dealerSocketService.connect(token);
      
      // Listen for connection status
      const checkConnection = setInterval(() => {
        setIsConnected(dealerSocketService.getConnectionStatus());
      }, 1000);

      // Test connection after 2 seconds
      setTimeout(() => {
        dealerSocketService.testConnection();
        dealerSocketService.onTestResponse((response) => {
          console.log('Test response:', response);
        });
      }, 2000);

      return () => {
        clearInterval(checkConnection);
        dealerSocketService.disconnect();
      };
    }
  }, [token]);

  // Listen for notifications
  useEffect(() => {
    const unsubscribe = dealerSocketService.onNotification((notification) => {
      setNotifications(prev => [notification, ...prev]);
      
      // Show toast notification
      showNotificationToast(notification);
    });

    return unsubscribe;
  }, []);

  // Show notification toast
  const showNotificationToast = useCallback((notification) => {
    // Play notification sound
    notificationSound.playTypeSound(notification.type);
    
    // Show toast notification with custom styling
    toast.custom((t) => (
      <div
        style={{
          background: '#303134',
          color: '#ededed',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${getPriorityBorderColor(notification.priority)}`,
          minWidth: '300px',
          fontFamily: 'Clash Grotesk, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Priority indicator bar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: getPriorityColor(notification.priority)
          }}
        />
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ color: getPriorityColor(notification.priority), marginTop: '2px' }}>
            {getNotificationIcon(notification.type)}
          </div>
          
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '4px',
              color: '#ededed'
            }}>
              {notification.title}
            </div>
            <div style={{ 
              fontSize: '13px', 
              color: '#B4B4B4',
              lineHeight: '1.4'
            }}>
              {notification.message}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#9E9E9E',
              marginTop: '8px'
            }}>
              {new Date(notification.timestamp).toLocaleTimeString()}
            </div>
          </div>
          
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              background: 'none',
              border: 'none',
              color: '#9E9E9E',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              fontSize: '16px',
              lineHeight: 1
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.color = '#ededed';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#9E9E9E';
            }}
          >
            ×
          </button>
        </div>
      </div>
    ), {
      duration: 5000,
      position: 'top-right',
    });
  }, []);

  // Get notification type based on priority
  const getNotificationType = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'info';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#F43F60';
      case 'medium': return '#FFA726';
      case 'low': return '#4ECDC4';
      default: return '#9E9E9E';
    }
  };

  // Get priority border color
  const getPriorityBorderColor = (priority) => {
    switch (priority) {
      case 'high': return 'rgba(244, 63, 96, 0.3)';
      case 'medium': return 'rgba(255, 167, 38, 0.3)';
      case 'low': return 'rgba(78, 205, 196, 0.3)';
      default: return 'rgba(158, 158, 158, 0.3)';
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_offer': return '💰';
      case 'vehicle_update': return '🚗';
      case 'new_schedule': return '📅';
      case 'new_message': return '💬';
      case 'system_notification': return '🔔';
      default: return '📢';
    }
  };

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Remove specific notification
  const removeNotification = useCallback((index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  return {
    notifications,
    isConnected,
    clearNotifications,
    removeNotification,
    showNotificationToast
  };
}; 